import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useLanguageFromPath } from '../../hooks/useLanguageFromPath';
import { lp, getLangFromPath } from '../../utils/routes';
import { useLocation } from 'react-router-dom';
import FormHoneypot from '@/components/feature/FormHoneypot';
import TurnstileField, {
  type TurnstileFieldHandle,
} from '@/components/feature/TurnstileField';
import { isTurnstileConfigured } from '@/config/turnstileEnv';
import { honeypotFilled } from '@/lib/formSpamGuard';
import { SITE_ORIGIN } from '@/config/siteUrl';
import { submitContactApplication } from '@/services/submitToKailac';
import {
  KAILAC_TEL_HREF,
  KAILAC_WHATSAPP_NUMBER,
} from '@/config/contactPhone';

const WA_LINK = `https://wa.me/${KAILAC_WHATSAPP_NUMBER}`;

export default function ContactPage() {
  useLanguageFromPath();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const turnstileRef = useRef<TurnstileFieldHandle>(null);

  useEffect(() => {
    const isEs = i18n.language === 'es';
    document.title = isEs
      ? 'Solicitar Ahora | Asesoría de Financiamiento Empresarial | Kailac'
      : 'Apply Now | Start Your 0% Business Funding Application | Kailac';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', isEs
      ? 'Solicita ahora orientación experta en financiamiento empresarial al 0% de interés. Revisión gratuita del perfil en 48 horas. Sin compromiso. Solo para solicitantes calificados.'
      : 'Apply now for expert-guided 0% interest business funding. Qualified entrepreneurs submit a 5-minute application. Free profile review within 48 hours. No commitment required. Subject to eligibility.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', isEs ? `${SITE_ORIGIN}/es/contacto` : `${SITE_ORIGIN}/contact`);
    let hreflangEn = document.querySelector('link[hreflang="en-contact"]') as HTMLLinkElement | null;
    let hreflangEs = document.querySelector('link[hreflang="es-contact"]') as HTMLLinkElement | null;
    if (!hreflangEn) { hreflangEn = document.createElement('link'); hreflangEn.setAttribute('rel', 'alternate'); hreflangEn.setAttribute('hreflang', 'en'); hreflangEn.setAttribute('data-page', 'contact'); document.head.appendChild(hreflangEn); }
    if (!hreflangEs) { hreflangEs = document.createElement('link'); hreflangEs.setAttribute('rel', 'alternate'); hreflangEs.setAttribute('hreflang', 'es'); hreflangEs.setAttribute('data-page', 'contact'); document.head.appendChild(hreflangEs); }
    hreflangEn.setAttribute('href', `${SITE_ORIGIN}/contact`);
    hreflangEs.setAttribute('href', `${SITE_ORIGIN}/es/contacto`);
  }, [i18n.language]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    const form = e.currentTarget;
    const data = new FormData(form);
    if (honeypotFilled(data)) {
      navigate(lp('/thank-you', lang));
      return;
    }
    const payload: Record<string, string> = {};
    data.forEach((value, key) => {
      payload[key] = String(value);
    });

    setSubmitting(true);
    try {
      let captchaVerified = false;
      if (isTurnstileConfigured()) {
        const token = await turnstileRef.current?.ensureToken();
        if (!token) {
          setFormError(t('form_captcha_failed'));
          return;
        }
        captchaVerified = true;
      }

      try {
        await submitContactApplication(payload, {
          recaptchaVerified: captchaVerified,
        });
      } catch (_err) {
        /* continuar al agradecimiento aunque falle red/Firebase */
      }
      turnstileRef.current?.reset();
      navigate(lp('/thank-you', lang));
    } finally {
      setSubmitting(false);
    }
  };

  const quickActions = [
    {
      icon: 'ri-file-list-3-line',
      color: 'bg-[#F8F3FB] text-[#4F2D63]',
      label: t('contact_quick_form'),
      desc: t('contact_quick_form_desc'),
      href: '#apply-form',
      external: false,
    },
    {
      icon: 'ri-whatsapp-line',
      color: 'bg-[#ECFDF5] text-[#059669]',
      label: t('contact_quick_wa'),
      desc: t('contact_quick_wa_desc'),
      href: WA_LINK,
      external: true,
    },
    {
      icon: 'ri-phone-line',
      color: 'bg-amber-50 text-amber-700',
      label: t('contact_quick_call'),
      desc: t('contact_quick_call_desc'),
      href: KAILAC_TEL_HREF,
      external: false,
    },
    {
      icon: 'ri-mail-line',
      color: 'bg-[#F8F3FB] text-[#4F2D63]',
      label: t('contact_quick_email'),
      desc: t('contact_quick_email_desc'),
      href: 'mailto:info@kailac.com',
      external: false,
    },
  ];

  const steps = [
    { num: '1', title: t('contact_step_1_title'), desc: t('contact_step_1_desc') },
    { num: '2', title: t('contact_step_2_title'), desc: t('contact_step_2_desc') },
    { num: '3', title: t('contact_step_3_title'), desc: t('contact_step_3_desc') },
    { num: '4', title: t('contact_step_4_title'), desc: t('contact_step_4_desc') },
  ];

  const qaItems = [
    { q: t('contact_qa_1_q'), a: t('contact_qa_1_a') },
    { q: t('contact_qa_2_q'), a: t('contact_qa_2_a') },
    { q: t('contact_qa_3_q'), a: t('contact_qa_3_a') },
  ];

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#F8F3FB] pt-32 pb-14 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">{t('contact_eyebrow')}</p>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
            {t('contact_hero_title_1')}<br />
            <span className="text-[#4F2D63]">{t('contact_hero_title_2')}</span>
          </h1>
          <p className="text-base text-gray-500 max-w-2xl mx-auto mb-3">
            {t('contact_hero_subtitle')}
          </p>
          <p className="text-xs text-gray-400 italic">{t('contact_hero_disclaimer')}</p>
        </div>
      </section>

      {/* ── Quick Actions Bar ── */}
      <section className="py-8 px-6 lg:px-8 bg-white border-b border-[#F0E8F8]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest text-center mb-5">{t('contact_quick_title')}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {quickActions.map(({ icon, color, label, desc, href, external }) => {
              const handleClick = (e: React.MouseEvent) => {
                if (!external && href.startsWith('#')) {
                  e.preventDefault();
                  const el = document.querySelector(href);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              };
              return (
                <a
                  key={label}
                  href={href}
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noopener noreferrer' : undefined}
                  onClick={handleClick}
                  className="flex items-center gap-3 bg-[#FCFAFD] border border-[#E9D9F2] rounded-xl px-4 py-3.5 hover:border-[#4F2D63] hover:-translate-y-0.5 transition-all cursor-pointer group"
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${color}`}>
                    <i className={`${icon} text-base`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 leading-tight group-hover:text-[#4F2D63] transition-colors">{label}</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-tight">{desc}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <main className="py-14 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* ── Form ── */}
            <div className="lg:col-span-3" id="apply-form">
              <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8F3FB]">
                    <i className="ri-file-list-3-line text-[#4F2D63] text-lg"></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{t('contact_form_title')}</h2>
                    <p className="text-xs text-gray-500">{t('contact_form_subtitle')}</p>
                  </div>
                </div>
                <form
                  id="apply-now-form"
                  onSubmit={handleSubmit}
                  className="relative space-y-5"
                >
                  <FormHoneypot />
                  <input type="hidden" name="language" value={i18n.language === 'es' ? 'Spanish / Español' : 'English'} />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('contact_fn_label')}</label>
                      <input type="text" name="first_name" required placeholder={t('contact_fn_placeholder')} className="w-full px-3 py-2.5 rounded-lg border border-[#E9D9F2] text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#4F2D63] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('contact_ln_label')}</label>
                      <input type="text" name="last_name" required placeholder={t('contact_ln_placeholder')} className="w-full px-3 py-2.5 rounded-lg border border-[#E9D9F2] text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#4F2D63] transition-colors" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('contact_email_label')}</label>
                    <input type="email" name="email" required placeholder={t('contact_email_placeholder')} className="w-full px-3 py-2.5 rounded-lg border border-[#E9D9F2] text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#4F2D63] transition-colors" />
                    <p className="text-xs text-gray-400 mt-1">{t('contact_email_hint')}</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('contact_phone_label')}</label>
                    <input type="tel" name="phone" required placeholder={t('contact_phone_placeholder')} className="w-full px-3 py-2.5 rounded-lg border border-[#E9D9F2] text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#4F2D63] transition-colors" />
                    <p className="text-xs text-gray-400 mt-1">{t('contact_phone_hint')}</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('contact_biz_label')}</label>
                    <input type="text" name="business_name" required placeholder={t('contact_biz_placeholder')} className="w-full px-3 py-2.5 rounded-lg border border-[#E9D9F2] text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#4F2D63] transition-colors" />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('contact_industry_label')}</label>
                    <select name="industry" required className="w-full px-3 py-2.5 rounded-lg border border-[#E9D9F2] text-sm text-gray-900 focus:outline-none focus:border-[#4F2D63] transition-colors">
                      <option value="">{t('contact_industry_select')}</option>
                      <option>{t('contact_industry_1')}</option>
                      <option>{t('contact_industry_2')}</option>
                      <option>{t('contact_industry_3')}</option>
                      <option>{t('contact_industry_4')}</option>
                      <option>{t('contact_industry_5')}</option>
                      <option>{t('contact_industry_6')}</option>
                      <option>{t('contact_industry_7')}</option>
                      <option>{t('contact_industry_8')}</option>
                      <option>{t('contact_industry_9')}</option>
                      <option>{t('contact_industry_10')}</option>
                      <option>{t('contact_industry_11')}</option>
                      <option>{t('contact_industry_12')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('contact_amount_label')}</label>
                    <select name="funding_amount" required className="w-full px-3 py-2.5 rounded-lg border border-[#E9D9F2] text-sm text-gray-900 focus:outline-none focus:border-[#4F2D63] transition-colors">
                      <option value="">{t('contact_amount_select')}</option>
                      <option>{t('contact_amount_1')}</option>
                      <option>{t('contact_amount_2')}</option>
                      <option>{t('contact_amount_3')}</option>
                      <option>{t('contact_amount_4')}</option>
                      <option>{t('contact_amount_5')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('contact_years_label')}</label>
                    <select name="years_in_business" required className="w-full px-3 py-2.5 rounded-lg border border-[#E9D9F2] text-sm text-gray-900 focus:outline-none focus:border-[#4F2D63] transition-colors">
                      <option value="">{t('contact_years_select')}</option>
                      <option>{t('contact_years_1')}</option>
                      <option>{t('contact_years_2')}</option>
                      <option>{t('contact_years_3')}</option>
                      <option>{t('contact_years_4')}</option>
                      <option>{t('contact_years_5')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-2">{t('contact_plan_label')}</label>
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-[#E9D9F2] hover:border-[#DBB6EE] hover:bg-[#F8F3FB] transition-colors">
                        <input type="radio" name="preferred_plan" value="Starter Plan ($500 + 4.3% success fee after funding)" className="mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{t('contact_plan_starter_name')}</p>
                          <p className="text-xs text-gray-500">{t('contact_plan_starter_desc')}</p>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-[#E9D9F2] hover:border-[#DBB6EE] hover:bg-[#F8F3FB] transition-colors">
                        <input type="radio" name="preferred_plan" value="Full-Service Plan (3% upfront)" className="mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{t('contact_plan_full_name')}</p>
                          <p className="text-xs text-gray-500">{t('contact_plan_full_desc')}</p>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-[#E9D9F2] bg-[#F8F3FB] transition-colors">
                        <input type="radio" name="preferred_plan" value="Not sure — please help me decide" className="mt-1" defaultChecked />
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{t('contact_plan_notsure_name')}</p>
                          <p className="text-xs text-gray-500">{t('contact_plan_notsure_desc')}</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('contact_notes_label')}</label>
                    <textarea
                      name="notes"
                      rows={3}
                      maxLength={500}
                      placeholder={t('contact_notes_placeholder')}
                      className="w-full px-3 py-2.5 rounded-lg border border-[#E9D9F2] text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#4F2D63] resize-none transition-colors"
                    />
                    <p className="text-xs text-gray-400 mt-1">{t('contact_notes_hint')}</p>
                  </div>

                  <TurnstileField
                    ref={turnstileRef}
                    className="flex w-full justify-center sm:justify-start min-h-[65px] py-1"
                  />

                  {formError ? (
                    <div className="space-y-1">
                      <p className="text-sm text-red-600" role="alert">
                        {formError}
                      </p>
                      {isTurnstileConfigured() ? (
                        <p className="text-xs text-gray-500">{t('form_captcha_hint')}</p>
                      ) : null}
                    </div>
                  ) : null}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-base transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {submitting ? t('contact_submit_loading') : t('contact_submit_btn')}
                  </button>

                  <div className="flex flex-col gap-2 items-center">
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><i className="ri-lock-2-line text-[#0F766E]"></i> {t('contact_trust_secure')}</span>
                      <span className="flex items-center gap-1"><i className="ri-eye-off-line text-[#0F766E]"></i> {t('contact_trust_confidential')}</span>
                      <span className="flex items-center gap-1"><i className="ri-shield-check-line text-[#0F766E]"></i> {t('contact_trust_nocommit')}</span>
                    </div>
                    <p className="text-xs text-gray-400 text-center leading-relaxed">
                      {t('contact_legal_prefix')}{' '}
                      <Link to={lp('/privacy-policy', lang)} className="text-[#4F2D63] hover:underline cursor-pointer">{t('contact_legal_privacy')}</Link>{' '}
                      {t('contact_legal_and')}{' '}
                      <Link to={lp('/terms-of-service', lang)} className="text-[#4F2D63] hover:underline cursor-pointer">{t('contact_legal_terms')}</Link>.{' '}
                      {t('contact_legal_suffix')}
                    </p>
                  </div>
                </form>
              </div>
            </div>

            {/* ── Sidebar ── */}
            <div className="lg:col-span-2 space-y-5">

              {/* What Happens Next */}
              <div className="bg-[#F8F3FB] rounded-2xl p-7 border border-[#E9D9F2]">
                <h3 className="text-sm font-bold text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-arrow-right-circle-line text-emerald-700"></i>
                  {t('contact_sidebar_next_title')}
                </h3>
                <div className="space-y-4">
                  {steps.map(({ num, title, desc }) => (
                    <div key={num} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#4F2D63] text-white text-xs font-bold flex-shrink-0 mt-0.5">{num}</div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Connect Card — Phone + WhatsApp + Email ── */}
              <div className="bg-[#4F2D63] rounded-2xl p-7 text-white">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3E234F] mb-4">
                  <i className="ri-chat-smile-2-line text-[#DBB6EE] text-lg"></i>
                </div>
                <h3 className="text-lg font-bold mb-1">{t('contact_call_title')}</h3>
                <p className="text-sm text-white/70 mb-4 leading-relaxed">{t('contact_call_subtitle')}</p>

                {/* WhatsApp — primary CTA inside card */}
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 mb-2 rounded-xl bg-[#25D366] hover:bg-[#1EB455] text-white text-sm font-bold cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-whatsapp-line text-base"></i> {t('contact_wa_btn')}
                </a>
                <p className="text-xs text-white/50 text-center mb-3">{t('contact_wa_online')}</p>

                <div className="flex gap-2">
                  <a
                    href={KAILAC_TEL_HREF}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white/15 hover:bg-white/25 text-white text-xs font-semibold cursor-pointer transition-colors"
                  >
                    <i className="ri-phone-fill text-sm flex-shrink-0"></i> {t('contact_call_phone')}
                  </a>
                  <a
                    href="mailto:info@kailac.com"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-white/25 text-white text-xs font-semibold hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <i className="ri-mail-line text-sm"></i> {t('contact_call_email')}
                  </a>
                </div>
              </div>

              {/* Trust notice */}
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-amber-600 text-base mt-0.5 flex-shrink-0"></i>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Important:</strong> {t('contact_notice')}
                  </p>
                </div>
              </div>

              {/* FAQ Quick Hits */}
              <div className="bg-white rounded-2xl border border-[#E9D9F2] p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4">{t('contact_qa_title')}</h3>
                <div className="space-y-3">
                  {qaItems.map(({ q, a }) => (
                    <div key={q}>
                      <p className="text-xs font-semibold text-gray-700">{q}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{a}</p>
                    </div>
                  ))}
                </div>
                <Link to={lp('/faq', lang)} className="mt-4 text-xs text-[#4F2D63] font-semibold hover:text-[#3E234F] flex items-center gap-1 cursor-pointer">
                  {t('contact_qa_link')} <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
